import React from "react";
import { ExternalLink, AlertTriangle, TrendingDown } from "lucide-react";
import { fmtEUR } from "@/lib/format";

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

function durationLabel(months) {
    if (!months) return null;
    return months >= 12
        ? `${Math.round((months / 12) * 10) / 10} año(s)`
        : `${months} meses`;
}

function BudgetCell({ r }) {
    if (r.pbl != null) {
        return (
            <div className="flex flex-col items-end">
                <span className="text-sm font-bold text-ink-950">{fmtEUR(r.pbl)}</span>
                <span className="text-[10px] text-ink-400 font-bold uppercase tracking-wider">
                    PBL · sin IVA
                </span>
                {r.duration_months ? (
                    <span className="text-[10px] text-ink-400 mt-0.5">
                        {durationLabel(r.duration_months)}
                    </span>
                ) : null}
            </div>
        );
    }
    if (r.budget != null) {
        return (
            <div className="flex flex-col items-end">
                <span className="text-sm">{fmtEUR(r.budget)}</span>
                <span
                    className="text-[10px] text-amber-700 font-bold uppercase tracking-wider"
                    title="Importe del feed PLCSP (suele ser valor estimado con prórrogas)"
                >
                    VEC · estimado
                </span>
                {r.duration_months ? (
                    <span className="text-[10px] text-ink-400 mt-0.5">
                        {durationLabel(r.duration_months)}
                    </span>
                ) : null}
            </div>
        );
    }
    return "—";
}

function DiscountCell({ r }) {
    if (r.discount_pct == null) {
        return <span className="text-ink-400">—</span>;
    }
    const badgeCls = r.discount_warning
        ? "bg-amber-50 text-amber-800 border border-amber-300"
        : r.discount_pct > 0
          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
          : "bg-slate-50 text-slate-600 border border-slate-200";
    const title = r.discount_warning
        ? `Baja calculada sobre el VALOR ESTIMADO (VEC). El contrato puede ser plurianual y el adjudicatario cobrar el importe anual — la baja real probablemente sea menor que ${r.discount_pct}%.`
        : `Baja calculada sobre el PBL${r.discount_basis === "PBL" ? "" : " (VEC, sin PBL disponible)"}.`;
    const subLabel =
        r.discount_basis === "PBL"
            ? "sobre PBL"
            : r.discount_warning
              ? "no fiable"
              : "sobre VEC";
    const subCls =
        r.discount_basis === "PBL" ? "text-emerald-700" : "text-amber-700";
    return (
        <div className="flex flex-col items-end gap-0.5">
            <span
                className={`inline-flex items-center gap-1 font-mono font-bold text-xs px-2 py-0.5 rounded-full ${badgeCls}`}
                title={title}
            >
                {r.discount_warning ? (
                    <AlertTriangle size={11} />
                ) : (
                    <TrendingDown size={11} />
                )}
                {r.discount_pct} %
            </span>
            <span
                className={`text-[9px] font-bold uppercase tracking-wider ${subCls}`}
            >
                {subLabel}
            </span>
        </div>
    );
}

export default function CompetitorResultsTable({ results }) {
    if (!results.length) return null;
    return (
        <div
            className="bg-white border border-ink-100 rounded-2xl overflow-hidden"
            data-testid="competitor-results-table"
        >
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-ink-50/60 text-[10px] font-bold uppercase tracking-wider text-ink-500">
                        <tr>
                            <th className="px-4 py-3 text-left">Licitación</th>
                            <th className="px-4 py-3 text-left">Adjudicataria</th>
                            <th className="px-4 py-3 text-right">Presupuesto</th>
                            <th className="px-4 py-3 text-right">Adjudicado</th>
                            <th className="px-4 py-3 text-right">Baja</th>
                            <th className="px-4 py-3 text-left">Fecha</th>
                            <th className="w-10"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-ink-100">
                        {results.map((r, i) => (
                            <tr
                                key={`${r.tender_id}-${i}`}
                                className="hover:bg-ink-50/40"
                                data-testid={`competitor-row-${i}`}
                            >
                                <td className="px-4 py-3 max-w-md">
                                    <div className="font-medium text-ink-950 line-clamp-2">
                                        {r.tender_title}
                                    </div>
                                    <div className="text-xs text-ink-500 mt-0.5 truncate">
                                        {r.tender_organo}
                                        {r.tender_expediente &&
                                            ` · ${r.tender_expediente}`}
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="font-bold text-sm text-ink-950">
                                        {r.party_name}
                                    </div>
                                    <div className="text-[11px] font-mono text-ink-400">
                                        {r.party_id}
                                    </div>
                                </td>
                                <td className="px-4 py-3 font-mono text-right whitespace-nowrap">
                                    <BudgetCell r={r} />
                                </td>
                                <td className="px-4 py-3 font-mono text-right font-bold whitespace-nowrap">
                                    {r.awarded_amount != null
                                        ? fmtEUR(r.awarded_amount)
                                        : "—"}
                                </td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <DiscountCell r={r} />
                                </td>
                                <td className="px-4 py-3 text-xs text-ink-600 whitespace-nowrap">
                                    {r.award_date ? fmtDate(r.award_date) : "—"}
                                </td>
                                <td className="px-4 py-3">
                                    {r.tender_link && (
                                        <a
                                            href={r.tender_link}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-ink-400 hover:text-brand-600"
                                            title="Abrir en PLCSP"
                                        >
                                            <ExternalLink size={14} />
                                        </a>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
